import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Link2, Mail } from 'lucide-react';
import type { EmailKeyUserRow } from '@/lib/users-roles/types';
import { rowNeedsMigration } from '@/lib/users-roles/problem-filter';
import { GrantChips } from './GrantChips';
import { LifecycleBadge } from './LifecycleBadge';

export function UsersRolesMobileCard({
  row,
  selected,
  onToggleSelected,
  copyingEmail,
  sendingEmail,
  migratingEmail,
  onManage,
  onCopyLink,
  onSendEmail,
  onRestore,
  onMigrate,
  onRevokeAdmin,
}: {
  row: EmailKeyUserRow;
  selected?: boolean;
  onToggleSelected?: (emailKey: string) => void;
  copyingEmail: string | null;
  sendingEmail?: string | null;
  migratingEmail: string | null;
  onManage: (row: EmailKeyUserRow) => void;
  onCopyLink: (emailKey: string) => void;
  onSendEmail: (emailKey: string) => void;
  onRestore: (row: EmailKeyUserRow) => void;
  onMigrate: (row: EmailKeyUserRow) => void;
  onRevokeAdmin?: (role: EmailKeyUserRow['roles'][number]) => void;
}) {
  const isCopying = copyingEmail === row.emailKey;
  const isSending = sendingEmail === row.emailKey;
  const isMigrating = migratingEmail === row.emailKey;

  return (
    <Card className="overflow-hidden min-w-0" data-testid={`users-roles-mobile-card-${row.emailKey}`}>
      <CardContent className="p-4 space-y-3">
        <div className="min-w-0 space-y-2">
          <div className="flex items-start gap-3">
            {onToggleSelected ? (
              <Checkbox
                className="mt-1"
                aria-label={`Select ${row.emailKey}`}
                data-testid={`mobile-select-user-${row.emailKey}`}
                checked={selected === true}
                onCheckedChange={() => onToggleSelected(row.emailKey)}
              />
            ) : null}
            <button
              type="button"
              className="block w-full min-w-0 break-all text-left font-medium"
              onClick={() => onManage(row)}
              data-testid={`users-roles-mobile-card-open-${row.emailKey}`}
            >
              {row.emailKey}
            </button>
          </div>
          {row.displayName ? (
            <p className="text-sm text-muted-foreground">{row.displayName}</p>
          ) : null}
          <GrantChips
            grants={row.grants}
            onRevokeAdmin={onRevokeAdmin}
            revokeAlwaysVisible
          />
          <LifecycleBadge row={row} />
        </div>

        <Button
          className="h-11 w-full"
          onClick={() => onManage(row)}
          data-testid={`manage-user-${row.emailKey}`}
          aria-label={`Manage ${row.emailKey}`}
        >
          Manage
        </Button>

        {row.invitedNotSignedIn ? (
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-11"
              data-testid={`mobile-copy-sign-in-link-${row.emailKey}`}
              onClick={() => onCopyLink(row.emailKey)}
              disabled={isCopying}
            >
              {isCopying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
              Copy link
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-11"
              data-testid={`mobile-send-sign-in-email-${row.emailKey}`}
              onClick={() => onSendEmail(row.emailKey)}
              disabled={isSending}
            >
              {isSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Send email
            </Button>
          </div>
        ) : null}

        {rowNeedsMigration(row) ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-11 w-full"
            data-testid={`mobile-migrate-user-${row.emailKey}`}
            aria-label={`Migrate ${row.emailKey}`}
            disabled={isMigrating}
            onClick={() => onMigrate(row)}
          >
            {isMigrating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Migrate
          </Button>
        ) : null}

        {row.deleted ? (
          <Button
            type="button"
            className="h-11 w-full"
            data-testid={`mobile-restore-user-${row.emailKey}`}
            onClick={() => onRestore(row)}
          >
            Restore
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
